import { forwardRef, ReactElement, useState } from "react";
import { cn } from "@repo/ui/lib/utils";
import Icon, { IconName } from "@repo/ui/components/ui/icon/icon";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  rounded?: boolean;
  slim?: boolean;
  label?: string | ReactElement;
  id?: string;
  type?: string;
  value?: string;
  ref?: any;
  icon?: IconName;
  actionIcon?: boolean;
  onActionIconClick?: () => void;
  actionButton?: boolean;
  actionButtonText?: string;
  // eslint-disable-next-line no-unused-vars
  onActionButtonClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      id,
      label,
      type = "text",
      value,
      required,
      rounded,
      actionIcon,
      onActionIconClick,
      actionButton,
      actionButtonText,
      onActionButtonClick,
      slim,
      icon,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <div className="relative flex flex-row">
        {!!icon && (
          <Icon
            onClick={() => onActionIconClick?.()}
            className={cn(
              "stroke-primary-magenta fill-primary-magenta flex items-center absolute left-0 px-[30px] max-md:px-6 h-full *:h-[24px] *:w-[24px] *:max-md:h-[16px] *:max-md:w-[16px]",
              { "px-3": slim },
              { "cursor-pointer": actionIcon },
            )}
            icon={icon}
          ></Icon>
        )}
        <label
          htmlFor={id}
          hidden={!!value || isFocused}
          className={cn(
            "!normal-case absolute top-[50%] translate-y-[-50%] left-3 text-xl max-md:text-base text-primary-black",
            {
              "text-sm": slim,
              "left-10": rounded,
              "left-[84px] max-md:left-[64px]": icon,
              "left-12": slim && icon,
            },
          )}
        >
          {label}
        </label>
        <input
          id={id}
          type={type}
          className={cn(
            "flex h-[70px] px-3 w-full rounded-lg bg-secondary-gray2 dark:bg-primary-white text-primary-black py-2 text-xl ring-offset-background",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed",
            "disabled:opacity-50",
            className,
            {
              "rounded-[100px] px-10 max-md:h-[60px] bg-secondary-gray2 dark:bg-primary-white text-primary-black max-md:text-base":
                rounded,
              "h-[48px] max-md:h-[48px] text-sm pl-3": slim,
              "pl-[84px] max-md:pl-[64px]": icon,
              "pl-12": slim && icon,
            },
          )}
          required={required}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          ref={ref}
          value={value}
          {...props}
        />
        {!!actionButton && (
          <button
            className={cn(
              "bg-primary-black text-primary-white rounded-r translate-x-[-100%] px-[20px]",
              {
                "rounded-r-[100px]": rounded,
              },
            )}
            onClick={onActionButtonClick}
          >
            {actionButtonText}
          </button>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
